import { Column, Container, Row } from '@xo-union/tk-component-grid';
import Spinner from '@xo-union/tk-component-spinner';
import React, { useContext, useEffect, useState } from 'react';

import VariantContext from '../../VariantContext';
import styles from './changeThemeRail.scss';
import ThemeCard, { Theme, ThemeFamily } from './ThemeCard';

import { InlineAlert } from '@xo-union/tk-component-alerts';

interface ThemesListProps {
  themes: ThemeFamily[];
  currentFamily: ThemeFamily | null;
  selectedThemeId: number | null;
  onClick: (themeId: number) => void;
}

export const ThemesList: React.FC<ThemesListProps> = ({ themes, currentFamily, selectedThemeId, onClick }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadingDelay = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => {
      clearTimeout(loadingDelay);
    };
  }, []);

  if (loading) {
    return <Spinner className={styles.spinner} />;
  }

  return (
    <Row>
      {themes.map(t => {
        const family = t as ThemeFamily;

        if (family.themes && (!currentFamily?.themes || family.themes[0].id !== currentFamily.themes[0].id)) {
          return (
            <Column xs="12" xl="6" key={family.themes[0].id}>
              <ThemeCard
                themeFamily={family}
                onClick={onClick}
                selected={themeFamilyIsSelected(family, selectedThemeId) ? selectedThemeId : null}
              />
            </Column>
          );
        } else {
          return null;
        }
      })}
    </Row>
  );
};

export const lookupThemeFamily = (id: number | null, themeFamilies: ThemeFamily[]): ThemeFamily | null => {
  const currentFamily = themeFamilies.find(family => {
    const currentTheme = family.themes?.find(theme => theme.id === id);
    return currentTheme ? currentTheme : null;
  }) as ThemeFamily | null;
  return currentFamily;
};

export const lookupTheme = (id: number | null, themeFamilies: ThemeFamily[]): Theme | null => {
  for (const family of themeFamilies) {
    if (family.themes) {
      for (const theme of family.themes) {
        if (theme.id === id) {
          return theme as Theme;
        }
      }
    }
  }
  return null;
};

// is this theme or any of its colorways currently being previewed?
const themeFamilyIsSelected = (family: ThemeFamily, selectedThemeId: number | null) => {
  return selectedThemeId && family?.themes?.find(t => t.id === selectedThemeId);
};

interface ChangeThemeRailProps {
  previewTheme: (themeId: number, theme: Theme) => void;
  currentThemeId: number | null;
  selectedThemeId: number | null;
}

const ChangeThemeRail: React.FC<ChangeThemeRailProps> = ({ previewTheme, currentThemeId, selectedThemeId }) => {
  const { themes, loading } = useContext(VariantContext);

  const previewThemeHandler = (themeId: number) => {
    const theme = themes ? lookupThemeFamily(themeId, themes) : null;
    previewTheme(themeId, theme as Theme);
  };

  // find the current theme (and its related colorways) from the themes data
  const lookupCurrentFamily = () => {
    return themes ? lookupThemeFamily(currentThemeId, themes) : null;
  };

  const currentFamily = lookupCurrentFamily();

  const currentTheme = lookupTheme(currentThemeId, themes);

  if (loading || !themes.length) {
    return <Spinner className={styles.spinner} />;
  } else {
    return (
      <Container className={styles.themeGrid}>
        {!!currentFamily ? (
          <>
            <Row>
              <Column xs="12">
                <div className={styles.currentThemeLabel}>Current design</div>
              </Column>
            </Row>
            <Row>
              <Column xs="12">
                {currentFamily.themes?.length ? (
                  <ThemeCard
                    themeFamily={currentFamily}
                    onClick={previewThemeHandler}
                    featured={currentThemeId}
                    selected={themeFamilyIsSelected(currentFamily, selectedThemeId) ? selectedThemeId : null}
                  />
                ) : null}

                <Row>
                  {currentTheme?.active ? null : (
                    <InlineAlert type="warning">
                      This design is no longer available. If you switch designs, you won't be able to revert back.
                    </InlineAlert>
                  )}
                </Row>

                <div className={styles.divider} />
              </Column>
            </Row>
          </>
        ) : null}
        <ThemesList
          themes={themes}
          currentFamily={currentFamily}
          selectedThemeId={selectedThemeId}
          onClick={previewThemeHandler}
        />
      </Container>
    );
  }
};

export default React.memo(ChangeThemeRail);
